import { useContext, useState, ChangeEvent } from 'react';
import { useNavigate } from 'react-router';
import { useTranslation } from 'react-i18next';

import { Popup } from '@/components';
import { User } from '@/tmp';

import './SettingsPopup.css';
import { isPhoneOrHighScreen } from '@/utilities/sizeUtils';
import { checkCookiebot } from '@/utilities/checkCookiebot';

import { Box, IconButton, Typography, Paper, Switch } from '@mui/material';
import { ctxUser } from '@/contexts/UserContext';
import { AccountCircle, Cookie, DisplaySettings, Edit, FingerprintOutlined, Logout, Close } from '@mui/icons-material';
import LanguageSelect from './LanguageSelect';
import { BiometicLoginSupport } from '@/types';
import TargetAttributesPopup from '@/components/targetAttributes/TargetAttributesPopup';
import { NewFeatureBadge } from '@/components/common/NewFeatureBadge';

function openCookieManager() {
  Cookiebot.renew();
}

export default function SettingsPopup({ closePopup }: { closePopup: () => Promise<void> }) {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const ctx = useContext(ctxUser);
  const [showTargetAttributes, setShowTargetAttributes] = useState(false);
  const [bio, setBio] = useState<BiometicLoginSupport>(() => {
    const biometric = localStorage.getItem('biometriclogin');
    if (biometric === null) return 'false';
    if (biometric === 'notsupported') return 'notsupported';
    if (biometric === 'off') return 'off';
    return biometric === 'true' ? 'true' : 'false';
  });

  function handleBioChange(_event: ChangeEvent<HTMLInputElement>, checked: boolean) {
    setBio(checked ? 'true' : 'off');
    localStorage.setItem('biometriclogin', checked ? 'true' : 'off');
  }

  function isNotTokenUser() {
    return !ctx?.isToken();
  }

  function isTargetAttributesEditorEnabled() {
    return ctx?.hasExtraFeature('extrafeature_pluto_targetattributes_tab');
  }

  if (!ctx) return;

  const title = t('mainMenu.settings');
  const user = ctx.user as User;

  function CloseComponent() {
    return (
      <IconButton
        className="close-button"
        aria-label="close"
        onClick={closePopup}
        sx={{ color: 'var(--mui-palette-text-primary)' }}
        data-qa-element-id="popup-close-button"
      >
        <Close />
      </IconButton>
    );
  }

  const body = (
    <div>
      {isNotTokenUser() && (
        <div>
          <Typography className="settings-block-title" data-qa-element-id="profile_title_label">
            {t('settings.profile')}
          </Typography>
          <Paper className="settings-box" data-qa-element-id="profile_paper">
            <AccountCircle className="icon-left" />
            <Box className="box">
              <Typography className="settings-text" data-qa-element-id="username_label">
                {user.name}
              </Typography>
              <Typography className="settings-subtext" data-qa-element-id="email_label">
                {user.email}
              </Typography>
            </Box>
            <IconButton onClick={async () => await navigate('/logout')} data-qa-element-id="profle_logout_button">
              <Logout className="icon-right" />
            </IconButton>
          </Paper>
        </div>
      )}
      <div>
        <Typography className="settings-block-title" data-qa-element-id="language_title_label">
          {t('settings.language')}
        </Typography>
        <LanguageSelect />
      </div>
      {bio !== 'notsupported' && isNotTokenUser() && (
        <div>
          <Typography className="settings-block-title" data-qa-element-id="biometrics_title_label">
            {t('settings.biometrics')}
          </Typography>
          <Paper className="settings-box" data-qa-element-id="biometrics_paper">
            <FingerprintOutlined className="icon-left" />
            <Box className="box">
              <Typography className="settings-text" data-qa-element-id="biometrics_label">
                {t('settings.biometricAuthentication')}
              </Typography>
            </Box>
            <Switch
              color="secondary"
              checked={bio !== 'off'}
              onChange={handleBioChange}
              data-qa-element-id="biometrics_swich_button"
            />
          </Paper>
        </div>
      )}
      {checkCookiebot() && isNotTokenUser() && (
        <div>
          <Typography className="settings-block-title" data-qa-element-id="cookies_title_label">
            {t('settings.cookies')}
          </Typography>
          <Paper className="settings-box" elevation={1} data-qa-element-id="cookies_paper">
            <Cookie className="icon-left" />
            <Box className="box">
              <Typography className="settings-text" data-qa-element-id="cookies_label">
                {t('settings.cookieManagement')}
              </Typography>
            </Box>
            <IconButton onClick={openCookieManager} data-qa-element-id="open_cookies_button">
              <Edit className="icon-right" />
            </IconButton>
          </Paper>
        </div>
      )}
      {!isPhoneOrHighScreen() && isTargetAttributesEditorEnabled() && isNotTokenUser() && (
        <div>
          <Typography className="settings-block-title" data-qa-element-id="target_attributes_title_label">
            {t('settings.targetAttributes')}
          </Typography>
          <Paper
            className={showTargetAttributes ? 'settings-box settings-box-selected' : 'settings-box'}
            data-qa-element-id="taregt_attributes_paper"
          >
            <NewFeatureBadge releaseDate="2026-01-25" top="0px" right="32px">
              <DisplaySettings className="icon-left" />
            </NewFeatureBadge>
            <Box className="box">
              <Typography className="settings-text" data-qa-element-id="target_attributes_label">
                {t('settings.editTargetAttributes')}
              </Typography>
            </Box>
            <IconButton
              color="primary"
              onClick={() => setShowTargetAttributes(!showTargetAttributes)}
              data-qa-element-id="open_target_attributes_button"
            >
              <Edit className="icon-right" />
            </IconButton>
          </Paper>
        </div>
      )}
    </div>
  );

  if (isPhoneOrHighScreen()) {
    return (
      <Popup
        popupTitle={title}
        header={undefined}
        body={body}
        footer={undefined}
        closePopup={closePopup}
        isFullScr={true}
        showCloseButton={true}
        leftText={true}
      />
    );
  }

  return (
    <>
      <div className="settings-container" data-qa-element-id="settings_view">
        <div className="settings-header">
          <Typography variant="h6" className="settings-container-title" data-qa-element-id="settings_view_title">
            {title}
          </Typography>
          <CloseComponent />
        </div>
        {body}
      </div>
      {showTargetAttributes && <TargetAttributesPopup closePopup={closePopup} />}
    </>
  );
}
